import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { AvailabilityParams, AvailabilityResponse } from '@/client/types/entity/availability';

/**
 * Query hooks for the admin listing (entity) moderation surface —
 * `GET /api/admin/entities/*` (see ff-api admin/view/entity/entity.py).
 *
 * ff-api wraps most payloads in a `{ status, data }` envelope; a few of these
 * endpoints return the object at the top level. `unwrap` accepts either.
 */

const BASE = '/api/admin/entities';

const unwrap = <T,>(raw: any): T =>
  raw != null && typeof raw === 'object' && 'status' in raw && 'data' in raw
    ? (raw.data as T)
    : (raw as T);

const buildQs = (params?: Record<string, unknown>): string => {
  if (!params) return '';
  const clean = Object.entries(params).reduce<Record<string, string>>(
    (acc, [k, v]) => {
      if (v !== undefined && v !== null && v !== '') acc[k] = String(v);
      return acc;
    },
    {}
  );
  const qs = new URLSearchParams(clean).toString();
  return qs ? `?${qs}` : '';
};

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Pagination {
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

/** Minimal asset shape returned as a listing's cover — feed to `getAsset()`. */
export interface CoverAsset {
  uuid: string | null;
  uuid_uc: string | null;
  public_url: string | null;
  public_url_uc: string | null;
  path: string | null;
  path_root: string | null;
  mime_type: string | null;
}

export interface ReviewQueueItem {
  eid: number;
  name: string | null;
  uuid: string | null;
  etid: number | null;
  type_name: string | null;
  status: string | null;
  date_added: string | null;
  date_status_changed: string | null;
  uid: number | null;
  oid: number | null;
  owner_name: string | null;
  cover?: CoverAsset | null;
}

export interface ReviewQueueResponse {
  total: number;
  queue: ReviewQueueItem[];
  pagination: Pagination;
}

export interface ReportedEntity {
  eid: number;
  report_count: number;
  name: string | null;
  uuid: string | null;
  status: string | null;
  etid: number | null;
  uid: number | null;
  oid: number | null;
}

export interface ReportedResponse {
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  reported: ReportedEntity[];
}

// ─── Query keys ──────────────────────────────────────────────────────────────

export const entityKeys = {
  all: ['admin-entities'] as const,
  detail: (eid: number) => [...entityKeys.all, 'detail', eid] as const,
  availability: (params: object) => [...entityKeys.all, 'availability', params] as const,
  reviewQueue: (params: object) => [...entityKeys.all, 'review-queue', params] as const,
  reported: (params: object) => [...entityKeys.all, 'reported', params] as const,
  history: (eid: number) => [...entityKeys.all, 'history', eid] as const,
  engagement: (eid: number, params: object = {}) => [...entityKeys.all, 'engagement', eid, params] as const,
  prices: (eid: number) => [...entityKeys.all, 'prices', eid] as const,
  priceHistory: (eid: number) => [...entityKeys.all, 'price-history', eid] as const,
  attributes: (eid: number) => [...entityKeys.all, 'attributes', eid] as const,
  payments: (eid: number) => [...entityKeys.all, 'payments', eid] as const,
  promotions: (eid: number) => [...entityKeys.all, 'promotions', eid] as const,
  assets: (eid: number) => [...entityKeys.all, 'assets', eid] as const,
};

// ─── Assets (media) ────────────────────────────────────────────────────────

export interface EntityAsset {
  asid: number;
  eid: number;
  uuid: string | null;
  uuid_uc: string | null;
  public_url: string | null;
  public_url_uc: string | null;
  path: string | null;
  path_root: string | null;
  mime_type: string | null;
  name: string | null;
  cover: number;
  default: number;
  order_by: number | null;
  deleted: number;
}

export interface EntityAssetsResponse {
  eid: number;
  assets: EntityAsset[];
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

const useReq = () => useClientStore((s) => s.request);

/** Full admin detail for a single listing (no public-visibility filtering). */
export const useEntityDetailQuery = (eid: number | null, enabled = true) => {
  const request = useReq();
  return useQuery({
    queryKey: entityKeys.detail(eid ?? -1),
    enabled: enabled && !!eid,
    queryFn: withErrorHandling(
      () => request<any>(`${BASE}/${eid}`).then((r) => unwrap<any>(r)),
      'Failed to load the listing'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

/** Live store-less goods listings of every owner and their "still available?" state. */
export const useAvailabilityQuery = (params: AvailabilityParams = {}) => {
  const request = useReq();
  return useQuery<AvailabilityResponse | undefined>({
    queryKey: entityKeys.availability(params),
    queryFn: withErrorHandling(
      () =>
        request<any>(`${BASE}/availability${buildQs(params)}`).then((r) =>
          unwrap<AvailabilityResponse>(r)
        ),
      'Failed to load listing availability'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

/**
 * Moderation queue: entities pending review, rejected, or suspended.
 * `status` is comma-separated (REVIEW,REJECTED,SUSPENDED,DRAFT).
 */
export const useReviewQueueQuery = (
  params: {
    status?: string;
    etid?: number;
    search?: string;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
    page?: number;
    page_size?: number;
  } = {},
  enabled = true
) => {
  const request = useReq();
  return useQuery({
    queryKey: entityKeys.reviewQueue(params),
    enabled,
    queryFn: withErrorHandling(
      () =>
        request<any>(`${BASE}/review-queue${buildQs(params)}`).then((r) =>
          unwrap<ReviewQueueResponse>(r)
        ),
      'Failed to load the review queue'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

/** Listings with open user reports (triage queue). */
export const useReportedEntitiesQuery = (
  params: { page?: number; page_size?: number; status?: string } = {},
  enabled = true
) => {
  const request = useReq();
  return useQuery({
    queryKey: entityKeys.reported(params),
    enabled,
    queryFn: withErrorHandling(
      () =>
        request<any>(`${BASE}/reported${buildQs(params)}`).then((r) =>
          unwrap<ReportedResponse>(r)
        ),
      'Failed to load reported listings'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

/** Change-history timeline for a listing (newest first). */
export const useEntityHistoryQuery = (eid: number | null, enabled = true) => {
  const request = useReq();
  return useQuery({
    queryKey: entityKeys.history(eid ?? -1),
    enabled: enabled && !!eid,
    queryFn: withErrorHandling(
      () => request<any>(`${BASE}/${eid}/history`).then((r) => unwrap<any>(r)),
      'Failed to load listing history'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

/** Window/rollup for the engagement timeseries; omit for the API's default
 *  last-30-days daily series. The counts block is unaffected by these. */
export interface EngagementParams {
  granularity?: 'daily' | 'weekly' | 'monthly';
  start_date?: string;
  end_date?: string;
}

/** Per-listing engagement counts (views/contacts/favorites/etc.). */
export const useEntityEngagementQuery = (eid: number | null, enabled = true, params: EngagementParams = {}) => {
  const request = useReq();
  const search = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '') as [string, string][],
  ).toString();

  return useQuery({
    queryKey: entityKeys.engagement(eid ?? -1, params),
    enabled: enabled && !!eid,
    queryFn: withErrorHandling(
      () => request<any>(`${BASE}/${eid}/engagement${search ? `?${search}` : ''}`).then((r) => unwrap<any>(r)),
      'Failed to load engagement'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

/** Prices for a listing. */
export const useEntityPricesQuery = (eid: number | null, enabled = true) => {
  const request = useReq();
  return useQuery({
    queryKey: entityKeys.prices(eid ?? -1),
    enabled: enabled && !!eid,
    queryFn: withErrorHandling(
      () => request<any>(`${BASE}/${eid}/price`).then((r) => unwrap<any>(r)),
      'Failed to load prices'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

/** Attributes/specs for a listing (includes inactive). */
export const useEntityAttributesQuery = (eid: number | null, enabled = true) => {
  const request = useReq();
  return useQuery({
    queryKey: entityKeys.attributes(eid ?? -1),
    enabled: enabled && !!eid,
    queryFn: withErrorHandling(
      () => request<any>(`${BASE}/${eid}/attributes`).then((r) => unwrap<any>(r)),
      'Failed to load attributes'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

/** Payments recorded against a listing. */
export const useEntityPaymentsQuery = (eid: number | null, enabled = true) => {
  const request = useReq();
  return useQuery({
    queryKey: entityKeys.payments(eid ?? -1),
    enabled: enabled && !!eid,
    queryFn: withErrorHandling(
      () => request<any>(`${BASE}/${eid}/payments`).then((r) => unwrap<any>(r)),
      'Failed to load payments'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

/** Active promotion(s) for a listing. */
export const useEntityPromotionsQuery = (eid: number | null, enabled = true) => {
  const request = useReq();
  return useQuery({
    queryKey: entityKeys.promotions(eid ?? -1),
    enabled: enabled && !!eid,
    queryFn: withErrorHandling(
      () => request<any>(`${BASE}/${eid}/promotions`).then((r) => unwrap<any>(r)),
      'Failed to load promotions'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

/**
 * All media on a listing, INCLUDING soft-deleted ones (so staff can restore).
 * Backed by `GET /api/admin/entities/<eid>/assets`.
 */
export const useEntityAssetsQuery = (eid: number | null, enabled = true) => {
  const request = useReq();
  return useQuery({
    queryKey: entityKeys.assets(eid ?? -1),
    enabled: enabled && !!eid,
    queryFn: withErrorHandling(
      () =>
        request<any>(`${BASE}/${eid}/assets`).then((r) =>
          unwrap<EntityAssetsResponse>(r)
        ),
      'Failed to load media'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

/**
 * Build the CSV export URL for the current filters. `GET /export` streams a CSV
 * attachment, so this is used as a direct link/`window.open` target rather than
 * a React Query hook.
 */
export const buildEntityExportUrl = (params?: {
  etid?: number;
  status?: string;
  limit?: number;
  test?: string;
}): string => `${BASE}/export${buildQs(params)}`;
